<template>
  <div
    ref="bar"
    :class="['editor-tabs', scroll ? 'j-tabs-scroller' : 'j-tabs-bar']"
    :data-mode="scroll ? 'scroll' : 'more'"
  >
    <div
      ref="list"
      class="j-tabs"
      role="tablist"
      :aria-label="label || _('Tabs')"
      v-on:keydown="onKeydown"
      v-on:focusin="onListFocusin"
      v-on:focusout="onFocusout"
      v-on:wheel="onWheel"
      v-on:pointerdown="onPointerdown"
    >
      <button
        v-for="tab in shownTabs"
        :id="tabElementId(tab.id)"
        :key="tab.id"
        :class="'j-tab' + (tab.disabled ? ' disabled' : '')"
        type="button"
        role="tab"
        :data-tab-id="tab.id"
        :aria-selected="tab.id === selectedId ? 'true' : 'false'"
        :aria-controls="panelIdPrefix ? panelIdPrefix + tab.id : undefined"
        :aria-disabled="tab.disabled ? 'true' : undefined"
        :tabindex="tabindexFor(tab.id)"
        :hidden="!visibleSet.has(tab.id)"
        v-on:click="activate(tab.id)"
        v-on:pointerenter="updateTitle"
        v-on:focus="updateTitle"
      >
        <slot name="tab" :tab="tab" :selected="tab.id === selectedId" :in-menu="false">
          <i v-if="tab.icon" :class="'fa fa-fw ' + tab.icon" aria-hidden="true"></i>
          <span class="j-tab-label">{{ tab.label }}</span>
          <span v-if="hasBadge(tab)" :class="'badge ' + (tab.badgeClass || 'btn-success')">{{ tab.badge }}</span>
        </slot>
      </button>
    </div>
    <TabOverflowMenu
      v-if="!scroll"
      ref="menu"
      :items="menuItems"
      :checked-id="selectedId"
      :label="moreLabel"
      :aria-label="moreAriaLabel"
      :menu-id="menuId"
      v-on:select="onMenuSelect"
    >
      <template #item="{ item, checked }">
        <slot name="tab" :tab="tabById(item.id)!" :selected="checked" :in-menu="true">
          <i v-if="tabById(item.id)?.icon" :class="'fa fa-fw ' + tabById(item.id)!.icon" aria-hidden="true"></i>
          <span class="editor-tabs-menu-label">{{ item.label }}</span>
          <span v-if="hasBadge(tabById(item.id))" :class="'badge ' + (tabById(item.id)!.badgeClass || 'btn-success')">{{ tabById(item.id)!.badge }}</span>
        </slot>
      </template>
    </TabOverflowMenu>
  </div>
</template>

<script setup lang="ts">
// Editor tab strip (JDEV-3208), replacing the hand-written <ul id="tabNavi" class="nav nav-tabs nav-justified">.
// One row that never wraps, in two modes by the primary pointer (usePointerMode, switched live):
// - 'coarse' (touch): a swipeable scroll strip, .j-tabs-scroller (useTabOverflow strategy "scroll"): native
//   swipe with scroll-snap, edge fades as the hint, no arrow buttons and no menu. The selected tab is scrolled
//   fully into view on load, on activation, on keyboard focus and after resizes.
// - 'fine' (mouse): .j-tabs-bar with the "Lisää (n)" menu pinned to its inline end (strategy "priority",
//   components/ui/TabOverflowMenu.vue, as the column header). The selected tab always stays in the row.
// Labels are never truncated, except the last resort: a selected label wider than the whole bar (fine mode)
// ends in an ellipsis and gets its full text as a title attribute; touch mode just scrolls.
// A mode switch keeps the selection and keyboard focus (focus in the menu falls back to the selected tab).
// WAI-ARIA tabs with roving tabindex and manual activation (useRovingTabs): Left/Right (mirrored in RTL),
// Home/End, Enter/Space; a click activates (never mousedown). Markup: shared/styles tabs.css (JDEV-3206);
// App-3 skin: .editor-tabs in assets/scss/johkupos-main-app.scss.
//
// Props:
//   tabs           [{ id, label, icon?, badge?, badgeClass?, disabled?, hidden? }] in order. icon is a Font
//                  Awesome class ('fa-credit-card'); badge a count or text shown as .badge.<badgeClass> (default
//                  btn-success; null, '' and 0 show nothing); disabled tabs are shown but cannot be activated
//                  (aria-disabled); hidden tabs are left out.
//   selected       the selected tab id (or v-model / modelValue)
//   label          the tablist's aria-label (default: "Tabs")
//   idPrefix       tab element ids are `${idPrefix}-tab-${id}`, for the panels' aria-labelledby (default: unique)
//   panelIdPrefix  set only when the panel ids are unique on the page: aria-controls = panelIdPrefix + id
// Attributes on the component (id="tabNavi", data-hint, data-hintPosition, data-position, style) go to the root.
// Events:   select(id) and update:modelValue(id) when another enabled tab is activated
// Slots:    tab { tab, selected, inMenu }  the content of a tab and of its menu row (default: icon, label, badge);
//                                          keep a .j-tab-label around the text
// Exposes:  refit(), reveal(id)

import { computed, nextTick, ref, useId, watch } from 'vue'
import TabOverflowMenu from '~/components/ui/TabOverflowMenu.vue'
import { usePointerMode } from '~/composables/usePointerMode'
import { useRovingTabs } from '~/composables/useRovingTabs'
import { useTabOverflow } from '~/composables/useTabOverflow'

export interface EditorTab {
  id: string
  label: string
  icon?: string
  badge?: string | number | null
  badgeClass?: string
  disabled?: boolean
  hidden?: boolean
}

const props = withDefaults(defineProps<{
  tabs: EditorTab[]
  selected?: string | null
  modelValue?: string | null
  label?: string
  idPrefix?: string
  panelIdPrefix?: string
}>(), { selected: undefined, modelValue: undefined, label: '', idPrefix: '', panelIdPrefix: '' })

const emit = defineEmits<{
  (e: 'select', id: string): void
  (e: 'update:modelValue', id: string): void
}>()

const { $translate } = useNuxtApp() as unknown as { $translate: (key: string) => string }
const _ = (key: string) => $translate(key)
// "context|Text" keys show their text part until translated (as ColumnTabs.vue).
const ctx = (key: string) => {
  const t = _(key)
  return t === key || t.includes('\u0004') ? key.slice(key.indexOf('|') + 1) : t
}

const bar = ref<HTMLElement | null>(null)
const list = ref<HTMLElement | null>(null)
const menu = ref<InstanceType<typeof TabOverflowMenu> | null>(null)
const toggle = computed(() => (menu.value?.toggleEl as HTMLElement | undefined) ?? null)

const pointer = usePointerMode()
const scroll = computed(() => pointer.value === 'coarse')
const strategy = computed(() => (scroll.value ? 'scroll' : 'priority'))

const shownTabs = computed(() => props.tabs.filter(t => t && !t.hidden))
const hasBadge = (tab?: EditorTab) => !!tab && tab.badge != null && tab.badge !== '' && tab.badge !== 0
// Editors build the tabs array in a computed or the template, so it is a new array on every render; the
// strip re-fits only when the ids or labels really change.
const idsKey = computed(() => shownTabs.value.map(t => t.id).join('\u0000'))
const ids = computed(() => (idsKey.value ? idsKey.value.split('\u0000') : []))
const layoutKey = computed(() => shownTabs.value.map(t => `${t.label}\u0001${t.icon || ''}\u0001${hasBadge(t) ? t.badge : ''}`).join('\u0000'))
const selectedId = computed(() => (props.selected !== undefined ? props.selected : props.modelValue) ?? null)

const { visibleIds, hiddenIds, refit, reveal } = useTabOverflow({
  bar,
  list,
  toggle,
  ids,
  selectedId,
  strategy,
  watch: [layoutKey]
})
const visibleSet = computed(() => new Set(visibleIds.value))

const { tabindexFor, onKeydown, onFocusin, onFocusout, focusTab } = useRovingTabs({
  list,
  ids: visibleIds,
  selectedId,
  onActivate: id => activate(id)
})

const autoId = useId()
const prefix = computed(() => props.idPrefix || `editor-tabs-${autoId}`)
const tabElementId = (id: string) => `${prefix.value}-tab-${id}`
const menuId = computed(() => `${prefix.value}-more`)
const tabById = (id: string) => shownTabs.value.find(t => t.id === id) as EditorTab | undefined
const menuItems = computed(() => hiddenIds.value.map(id => ({ id, label: tabById(id)?.label ?? id })))
const moreLabel = computed(() => ctx('tabs|More'))
const moreAriaLabel = computed(() => _('More, %d tabs hidden').replace('%d', String(hiddenIds.value.length)))

function activate (id: string) {
  const tab = tabById(id)
  if (!tab || tab.disabled || id === selectedId.value) return
  emit('select', id)
  emit('update:modelValue', id)
}

async function onMenuSelect (id: string) {
  activate(id)
  // The tab is swapped into the row; give it focus once it is shown.
  await nextTick()
  requestAnimationFrame(() => focusTab(id))
}

// Touch: a tab that gets keyboard focus slides fully into view. Not when the focus comes from a press or tap: the
// focus then arrives with the (compatibility) mousedown, and scrolling before the mouseup would move another tab
// under the finger or pointer, so the browser would send the click to the list instead of the tab. A pressed tab is
// revealed once it is selected (useTabOverflow reveals the selected tab).
const PRESS_FOCUS_MS = 1000
let pressedAt = -Infinity
function onPointerdown () {
  pressedAt = performance.now()
}
function onListFocusin (e: FocusEvent) {
  onFocusin(e)
  const id = (e.target as HTMLElement | null)?.closest<HTMLElement>('[data-tab-id]')?.dataset.tabId
  if (scroll.value && id && performance.now() - pressedAt > PRESS_FOCUS_MS) reveal(id)
}

// Touch mode used with a mouse wheel (Android with a mouse): a vertical wheel scrolls the strip sideways.
function onWheel (e: WheelEvent) {
  const el = list.value
  if (!scroll.value || !el || Math.abs(e.deltaY) <= Math.abs(e.deltaX) || el.scrollWidth <= el.clientWidth) return
  el.scrollLeft += e.deltaY * (getComputedStyle(el).direction === 'rtl' ? -1 : 1)
  e.preventDefault()
}

// The last resort for a label wider than the whole bar: it ends in an ellipsis, so give it a title.
function updateTitle (e: Event) {
  const tab = e.currentTarget as HTMLElement
  const label = tab.querySelector<HTMLElement>('.j-tab-label')
  if (label && label.scrollWidth > label.clientWidth + 1) tab.title = label.textContent?.trim() || ''
  else tab.removeAttribute('title')
}

// Mode switch: remember the focused tab before the strip is rebuilt, then focus it again (or the selected tab,
// if focus was in the menu or the tab is now in the menu).
watch(scroll, () => {
  const active = document.activeElement as HTMLElement | null
  if (!active || !bar.value?.contains(active)) return
  const tabId = active.closest<HTMLElement>('[role="tab"]')?.dataset.tabId ?? null
  nextTick(() => requestAnimationFrame(() => requestAnimationFrame(() => {
    const want = tabId && visibleSet.value.has(tabId) ? tabId : selectedId.value
    if (want) focusTab(want)
  })))
}, { flush: 'pre' })

defineExpose({ refit, reveal })
</script>
